import React from 'react';
import { Nullable } from 'global';
import { moneyService } from 'shared';
import { WarningRounded } from '@mui/icons-material';
import { Stack, Tooltip, Typography } from '@mui/material';

import { BreachTooltip } from './BreachTooltip';
import { QuoteCalcRowBreach } from '../../../../api/types';

interface Props {
  breach: Nullable<QuoteCalcRowBreach>;
}

const formatAllowedRange = (min: Nullable<number>, max: Nullable<number>): Nullable<string> => {
  if (min != null && max != null) return `${moneyService.formatUsd(min)}–${moneyService.formatUsd(max)}`;
  if (max != null) return `up to ${moneyService.formatUsd(max)}`;
  if (min != null) return `from ${moneyService.formatUsd(min)}`;

  return null;
};

const formatBreachLabel = (breach: QuoteCalcRowBreach): string => {
  if (breach.bound == null || breach.limit == null) return breach.message ?? 'Out of allowed range';

  return breach.bound === 'MAX'
    ? `Exceeds ${moneyService.formatUsd(breach.limit)} max`
    : `Below ${moneyService.formatUsd(breach.limit)} min`;
};

const WarningLine = ({ children }: { children: React.ReactNode }) => (
  <Stack direction="row" alignItems="center" gap="0.25rem">
    <WarningRounded color="warning" fontSize="small" />

    <Typography variant="caption" color="warning.main">
      {children}
    </Typography>
  </Stack>
);

export const BreachIndicator = ({ breach }: Props) => {
  if (!breach) return null;

  if (!breach.breached) {
    const range = formatAllowedRange(breach.min, breach.max);

    if (!range) return null;

    return (
      <Typography variant="caption" color="text.secondary">
        Allowed range: {range}
      </Typography>
    );
  }

  if (breach.clamped && breach.limit != null) {
    return (
      <WarningLine>
        Calculated: {moneyService.formatUsd(breach.enteredValue)} Applied: {moneyService.formatUsd(breach.limit)}
      </WarningLine>
    );
  }

  return (
    <Tooltip
      arrow
      placement="top"
      title={<BreachTooltip message={breach.message ?? formatBreachLabel(breach)} breakdown={breach.breakdown} />}
    >
      <WarningLine>{formatBreachLabel(breach)}</WarningLine>
    </Tooltip>
  );
};
